import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CalendarClock, Copy, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { translateFr as tfr } from "@/i18n";

const MEETING_TYPES = ["Revue de carrière", "Point contractuel", "Préparation mercato", "Rendez-vous club", "Point famille / entourage"].map((s) => tfr(s));

export function MeetingPrepDialog({ open, onOpenChange, playerId }) {
  const [type, setType] = useState(MEETING_TYPES[0]);
  const [objective, setObjective] = useState("");
  const [useAi, setUseAi] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/players/${playerId}/career-plan/meeting`, {
        meeting_type: type, objective: objective.trim(), use_ai: useAi,
      });
      setResult(r.data);
      if (useAi && !r.data.ai_agenda) toast.info(tfr("Brief généré — l'agenda IA n'est pas disponible pour le moment."));
    } catch (e) { toast.error(e?.response?.data?.detail || tfr("Impossible de préparer la réunion")); }
    finally { setLoading(false); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(tfr("Copié dans le presse-papiers"));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setResult(null); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="meeting-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarClock className="h-4 w-4 text-athlynx" /> {tfr("Préparer une réunion")}</DialogTitle>
          <DialogDescription>{tfr("Compile la situation réelle du dossier : objectifs, jalons, points de vigilance et questions à aborder.")}</DialogDescription>
        </DialogHeader>
        {!result ? (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{tfr("Type de réunion")}</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger data-testid="meeting-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MEETING_TYPES.map((t) => <SelectItem key={t} value={t}>{tfr(t)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{tfr("Objectif de la réunion (optionnel)")}</Label>
                <Input value={objective} onChange={(e) => setObjective(e.target.value)} maxLength={200}
                  placeholder={tfr("Ex. Valider la stratégie mercato")} data-testid="meeting-objective-input" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={useAi} onCheckedChange={(v) => setUseAi(!!v)} data-testid="meeting-ai-checkbox" />
              <span className="inline-flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-athlynx" /> Préparer avec Athlynx Intelligence (agenda + questions clés)
              </span>
            </label>
            <Button onClick={generate} disabled={loading} className="w-full bg-athlynx hover:bg-athlynx/90 text-white gap-2" data-testid="meeting-generate-btn">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Générer le brief de réunion
            </Button>
            <p className="text-[11px] text-muted-foreground">
              {tfr("Le brief est construit uniquement à partir des données enregistrées dans Athlynx — aucun fait n'est inventé.")}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl border border-border bg-secondary/30 p-4" data-testid="meeting-brief">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">{tfr("Brief de réunion")}</p>
                <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={() => copy(result.brief)} data-testid="meeting-copy-brief">
                  <Copy className="h-3 w-3" /> Copier
                </Button>
              </div>
              <pre className="text-xs whitespace-pre-wrap font-sans leading-relaxed">{result.brief}</pre>
            </div>
            {result.ai_agenda && (
              <div className="rounded-xl border border-athlynx/25 bg-athlynx/[0.04] p-4" data-testid="meeting-ai-agenda">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-athlynx inline-flex items-center gap-1.5">
                    <Sparkles className="h-3 w-3" /> Agenda proposé — Athlynx Intelligence
                  </p>
                  <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={() => copy(result.ai_agenda)} data-testid="meeting-copy-agenda">
                    <Copy className="h-3 w-3" /> Copier
                  </Button>
                </div>
                <pre className="text-xs whitespace-pre-wrap font-sans leading-relaxed">{result.ai_agenda}</pre>
              </div>
            )}
            <Button variant="outline" onClick={() => setResult(null)} className="w-full" data-testid="meeting-restart">{tfr("Préparer une autre réunion")}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
